// ============================================================================
// GAME ITEMS TYPE DEFINITIONS
// ============================================================================
// Types for in-game purchasable items and actions (like PokerStars gifts)
// Sound files are located in /public/sounds/items/
// ============================================================================

export type GameItemCategory = 'throwable' | 'self' | 'table' | 'taunt' | 'announce';

export interface GameItem {
  id: string;
  name: string;
  emoji: string;
  description: string;
  price: number; // PKCash cost (legal states)
  category: GameItemCategory;
  duration: number; // Animation duration in ms
  soundFile?: string; // Sound effect filename (in /sounds/items/)
  cooldown: number; // Cooldown in ms before same user can use again
  restrictedStatePrice?: number; // Higher price for restricted states
  restrictedStateCooldown?: number; // Longer cooldown for restricted states
  // Announce items: the table-wide cinematic line. {sender} and {target}
  // are replaced with the live names at render time.
  message?: string;
}

// ============================================================================
// THROWABLE ITEMS (target other players)
// ============================================================================
export const THROWABLE_ITEMS: GameItem[] = [
  {
    id: 'tomato',
    name: 'Tomato',
    emoji: '\u{1F345}',
    description: 'Throw a tomato at a player!',
    price: 0.10,
    category: 'throwable',
    duration: 2000,
    soundFile: 'splat.mp3',
    cooldown: 5000,
  },
  {
    id: 'egg',
    name: 'Egg',
    emoji: '\u{1F95A}',
    description: 'Egg someone!',
    price: 0.15,
    category: 'throwable',
    duration: 2000,
    soundFile: 'splat.mp3',
    cooldown: 5000,
  },
  {
    id: 'beer',
    name: 'Beer',
    emoji: '\u{1F37A}',
    description: 'Send a beer - cheers!',
    price: 0.25,
    category: 'throwable',
    duration: 2500,
    soundFile: 'cheers.mp3',
    cooldown: 5000,
  },
  {
    id: 'trophy',
    name: 'Trophy',
    emoji: '\u{1F3C6}',
    description: 'Award a trophy for good play!',
    price: 0.50,
    category: 'throwable',
    duration: 3000,
    soundFile: 'fanfare.mp3',
    cooldown: 10000,
  },
  {
    id: 'money',
    name: 'Money Stack',
    emoji: '\u{1F4B0}',
    description: 'Make it rain!',
    price: 0.75,
    category: 'throwable',
    duration: 3500,
    soundFile: 'money.mp3',
    cooldown: 10000,
  },
  {
    id: 'fish',
    name: 'Fish Slap',
    emoji: '\u{1F41F}',
    description: 'Slap with a fish - for the donkeys!',
    price: 0.20,
    category: 'throwable',
    duration: 2000,
    soundFile: 'slap.mp3',
    cooldown: 5000,
  },
  {
    id: 'kiss',
    name: 'Kiss',
    emoji: '\u{1F48B}',
    description: 'Blow a kiss!',
    price: 0.30,
    category: 'throwable',
    duration: 2500,
    soundFile: 'kiss.mp3',
    cooldown: 5000,
  },
  {
    id: 'rocket',
    name: 'Rocket',
    emoji: '\u{1F680}',
    description: 'To the moon!',
    price: 1.00,
    category: 'throwable',
    duration: 3000,
    soundFile: 'rocket.mp3',
    cooldown: 15000,
  },
];

// ============================================================================
// SELF ITEMS (on your own avatar)
// ============================================================================
export const SELF_ITEMS: GameItem[] = [
  {
    id: 'cigarette',
    name: 'Cigarette',
    emoji: '\u{1F6AC}',
    description: 'Light up a smoke',
    price: 0.10,
    category: 'self',
    duration: 8000,
    soundFile: 'smoke.mp3',
    cooldown: 10000,
  },
  {
    id: 'sunglasses',
    name: 'Sunglasses',
    emoji: '\u{1F60E}',
    description: 'Put on cool shades',
    price: 0.15,
    category: 'self',
    duration: 10000,
    soundFile: 'cool.mp3',
    cooldown: 15000,
  },
  {
    id: 'tophat',
    name: 'Top Hat',
    emoji: '\u{1F3A9}',
    description: 'Tip your hat',
    price: 0.20,
    category: 'self',
    duration: 3000,
    soundFile: 'tip.mp3',
    cooldown: 10000,
  },
  {
    id: 'lucky',
    name: 'Lucky Charm',
    emoji: '\u{1F340}',
    description: 'Show off your luck!',
    price: 0.25,
    category: 'self',
    duration: 5000,
    soundFile: 'sparkle.mp3',
    cooldown: 15000,
  },
  {
    id: 'fire',
    name: 'On Fire',
    emoji: '\u{1F525}',
    description: "You're on a hot streak!",
    price: 0.50,
    category: 'self',
    duration: 6000,
    soundFile: 'fire.mp3',
    cooldown: 20000,
  },
  {
    id: 'thinking',
    name: 'Deep Thought',
    emoji: '\u{1F914}',
    description: 'Hmm, tough decision...',
    price: 0.05,
    category: 'self',
    duration: 4000,
    soundFile: 'hmm.mp3',
    cooldown: 5000,
  },
  {
    id: 'crying',
    name: 'Crying',
    emoji: '\u{1F622}',
    description: 'Bad beat tears',
    price: 0.05,
    category: 'self',
    duration: 4000,
    soundFile: 'cry.mp3',
    cooldown: 5000,
  },
  {
    id: 'laughing',
    name: 'Laughing',
    emoji: '\u{1F602}',
    description: 'LOL!',
    price: 0.05,
    category: 'self',
    duration: 3000,
    soundFile: 'laugh.mp3',
    cooldown: 5000,
  },
];

// ============================================================================
// TAUNT ITEMS (free, generates tilt / table talk)
// Cooldowns are deliberately long (15-30s) per-item so players can't just
// mash YOU SUCK on every action. The target is the table-wide announcer:
// these render as a centered shout banner plus the emoji, no target needed.
// ============================================================================
export const TAUNT_ITEMS: GameItem[] = [
  {
    id: 'taunt_you_suck',
    name: 'You Suck!',
    emoji: '\u{1F44E}',
    description: 'Free shout - call out a bad play',
    price: 0,
    category: 'taunt',
    duration: 2500,
    soundFile: 'horn.mp3',
    cooldown: 20000,
  },
  {
    id: 'taunt_bad_beat',
    name: 'Bad Beat!',
    emoji: '\u{1F480}',
    description: 'Free shout - register your disgust',
    price: 0,
    category: 'taunt',
    duration: 2500,
    soundFile: 'splat.mp3',
    cooldown: 20000,
  },
  {
    id: 'taunt_donk',
    name: 'Donk!',
    emoji: '\u{1F434}',
    description: 'Free shout - for the donks at the table',
    price: 0,
    category: 'taunt',
    duration: 2500,
    soundFile: 'horn.mp3',
    cooldown: 25000,
  },
  {
    id: 'taunt_snap_call',
    name: 'Snap Call!',
    emoji: '\u{26A1}',
    description: 'Free shout - react to a quick call',
    price: 0,
    category: 'taunt',
    duration: 2000,
    cooldown: 15000,
  },
  {
    id: 'taunt_slowroll',
    name: 'Slowroll!',
    emoji: '\u{1F40C}',
    description: 'Free shout - call out the slowroll',
    price: 0,
    category: 'taunt',
    duration: 2500,
    cooldown: 20000,
  },
  {
    id: 'taunt_boring',
    name: 'Boring!',
    emoji: '\u{1F634}',
    description: 'Free shout - wake them up',
    price: 0,
    category: 'taunt',
    duration: 2000,
    cooldown: 25000,
  },
  {
    id: 'taunt_nh',
    name: 'Nice Hand',
    emoji: '\u{1F44F}',
    description: 'Free shout - pay respect (or sarcasm)',
    price: 0,
    category: 'taunt',
    duration: 2000,
    cooldown: 12000,
  },
  {
    id: 'taunt_lucky',
    name: 'Lucky!',
    emoji: '\u{1F340}',
    description: 'Free shout - accuse them of running hot',
    price: 0,
    category: 'taunt',
    duration: 2500,
    cooldown: 15000,
  },
  {
    id: 'taunt_sick',
    name: 'Sick One',
    emoji: '\u{1F92F}',
    description: 'Free shout - mind blown',
    price: 0,
    category: 'taunt',
    duration: 2500,
    cooldown: 15000,
  },
  {
    id: 'taunt_tilt',
    name: 'On Tilt!',
    emoji: '\u{1F624}',
    description: 'Free shout - broadcast their tilt',
    price: 0,
    category: 'taunt',
    duration: 2500,
    soundFile: 'horn.mp3',
    cooldown: 25000,
  },
  {
    id: 'taunt_chicken',
    name: 'Chicken!',
    emoji: '\u{1F414}',
    description: 'Free - for the folders',
    price: 0,
    category: 'taunt',
    duration: 2500,
    cooldown: 20000,
  },
  {
    id: 'taunt_bluff',
    name: 'Bluffing!',
    emoji: '\u{1F3AD}',
    description: 'Free - call the bluff',
    price: 0,
    category: 'taunt',
    duration: 2500,
    cooldown: 15000,
  },
  {
    id: 'taunt_cooler',
    name: 'Cooler!',
    emoji: '\u{1F9CA}',
    description: 'Free - it was just a cooler',
    price: 0,
    category: 'taunt',
    duration: 2500,
    cooldown: 15000,
  },
  {
    id: 'taunt_one_time',
    name: 'One Time!',
    emoji: '\u{1F64F}',
    description: 'Free - beg the deck',
    price: 0,
    category: 'taunt',
    duration: 2500,
    cooldown: 12000,
  },
  {
    id: 'taunt_gg',
    name: 'GG',
    emoji: '\u{1F91D}',
    description: 'Free - good game',
    price: 0,
    category: 'taunt',
    duration: 2000,
    cooldown: 12000,
  },
  {
    id: 'taunt_ship',
    name: 'Ship It!',
    emoji: '\u{1F6A2}',
    description: 'Free - ship the pot',
    price: 0,
    category: 'taunt',
    duration: 2500,
    cooldown: 15000,
  },
];

// ============================================================================
// ANNOUNCE ITEMS (paid, table-wide cinematic call-outs)
// ============================================================================
// The premium tilt product: pay to put a LINE on the table, addressed to a
// player, announced cinematically for everyone. Preset lines only (never
// free text - that's a moderation problem), priced high, long cooldowns.
export const ANNOUNCE_ITEMS: GameItem[] = [
  {
    id: 'announce_trash',
    name: "You're Trash",
    emoji: '\u{1F4E2}',
    description: 'Table-wide announcement calling them out',
    message: "Hey {target}... you're TRASH.",
    price: 0.50,
    category: 'announce',
    duration: 6000,
    soundFile: 'horn.mp3',
    cooldown: 30000,
  },
  {
    id: 'announce_scared',
    name: 'Scared Money',
    emoji: '\u{1F4E2}',
    description: 'Announce that they are playing scared',
    message: '{target} is playing SCARED MONEY.',
    price: 0.50,
    category: 'announce',
    duration: 6000,
    soundFile: 'horn.mp3',
    cooldown: 30000,
  },
  {
    id: 'announce_owned',
    name: 'Getting Owned',
    emoji: '\u{1F4E2}',
    description: 'Let the table know who owns whom',
    message: '{target} is getting OWNED by {sender}.',
    price: 0.75,
    category: 'announce',
    duration: 6000,
    soundFile: 'horn.mp3',
    cooldown: 45000,
  },
  {
    id: 'announce_bank',
    name: 'Personal ATM',
    emoji: '\u{1F4E2}',
    description: 'Declare them your personal ATM',
    message: '{sender} would like to thank {target}, the personal ATM.',
    price: 0.75,
    category: 'announce',
    duration: 6000,
    soundFile: 'money.mp3',
    cooldown: 45000,
  },
  {
    id: 'announce_king',
    name: 'Runs This Table',
    emoji: '\u{1F451}',
    description: 'Crown yourself, loudly',
    message: 'Bow down - {sender} RUNS this table.',
    price: 1.00,
    category: 'announce',
    duration: 6000,
    soundFile: 'fanfare.mp3',
    cooldown: 60000,
  },
];

// ============================================================================
// COMBINED & HELPERS
// ============================================================================

// All items combined
export const ALL_GAME_ITEMS: GameItem[] = [...THROWABLE_ITEMS, ...SELF_ITEMS, ...TAUNT_ITEMS, ...ANNOUNCE_ITEMS];

// Get item by ID
export const getGameItem = (itemId: string): GameItem | undefined => {
  return ALL_GAME_ITEMS.find(item => item.id === itemId);
};

// Active animation in Firestore
export interface GameItemAction {
  id: string; // Document ID
  tableId: string;
  itemId: string;
  senderId: string;
  senderName: string;
  targetId: string; // Same as senderId for self actions
  targetName: string;
  timestamp: Date;
  expiresAt: Date; // When animation should be removed
}

// For displaying in UI
export interface ActiveAnimation {
  id: string;
  itemId: string;
  item: GameItem;
  senderId: string;
  senderName: string;
  targetId: string;
  targetName: string;
  startTime: number;
}

// ============================================================================
// STATE-BASED PRICING HELPERS
// ============================================================================

// Default premium multipliers for restricted states
const RESTRICTED_PRICE_MULTIPLIER = 2.0; // 2x price
const RESTRICTED_COOLDOWN_MULTIPLIER = 2.0; // 2x cooldown
const MIN_RESTRICTED_PRICE = 0.15; // Minimum price floor

/**
 * Get item price based on state
 */
export const getItemPrice = (itemId: string, isRestrictedState: boolean): number => {
  const item = getGameItem(itemId);
  if (!item) return 0;

  if (isRestrictedState) {
    if (item.restrictedStatePrice !== undefined) {
      return item.restrictedStatePrice;
    }
    return Math.max(MIN_RESTRICTED_PRICE, item.price * RESTRICTED_PRICE_MULTIPLIER);
  }

  return item.price;
};

/**
 * Get item cooldown based on state
 */
export const getItemCooldown = (itemId: string, isRestrictedState: boolean): number => {
  const item = getGameItem(itemId);
  if (!item) return 5000;

  if (isRestrictedState) {
    if (item.restrictedStateCooldown !== undefined) {
      return item.restrictedStateCooldown;
    }
    return item.cooldown * RESTRICTED_COOLDOWN_MULTIPLIER;
  }

  return item.cooldown;
};

/**
 * Get all items with effective pricing for current state
 */
export const getItemsWithPricing = (isRestrictedState: boolean): (GameItem & { effectivePrice: number; effectiveCooldown: number })[] => {
  return ALL_GAME_ITEMS.map(item => ({
    ...item,
    effectivePrice: getItemPrice(item.id, isRestrictedState),
    effectiveCooldown: getItemCooldown(item.id, isRestrictedState),
  }));
};

/**
 * Get throwable items with effective pricing
 */
export const getThrowableItemsWithPricing = (isRestrictedState: boolean) => {
  return THROWABLE_ITEMS.map(item => ({
    ...item,
    effectivePrice: getItemPrice(item.id, isRestrictedState),
    effectiveCooldown: getItemCooldown(item.id, isRestrictedState),
  }));
};

/**
 * Get self items with effective pricing
 */
export const getSelfItemsWithPricing = (isRestrictedState: boolean) => {
  return SELF_ITEMS.map(item => ({
    ...item,
    effectivePrice: getItemPrice(item.id, isRestrictedState),
    effectiveCooldown: getItemCooldown(item.id, isRestrictedState),
  }));
};

/**
 * Get taunt items with effective pricing. Taunts are always free (price: 0)
 * regardless of state, so this just preserves the shape callers expect.
 */
export const getTauntItemsWithPricing = (_isRestrictedState: boolean) => {
  return TAUNT_ITEMS.map(item => ({
    ...item,
    effectivePrice: 0,
    effectiveCooldown: item.cooldown,
  }));
};
